import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {
	Button,
	Detail,
	FlexColumn,
	FlexRow,
	Icon,
	Label,
	Pill,
	PillVariant,
	Text,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaButtonSizer} from './NovaButtonSize';
import {NOVA_FILE_ACCEPTED_EXTENSIONS, fileTypeLabel} from '../shared/novaFileTypes';
import {NOVA_FILE_READY_STATUS} from '../shared/novaVariableLibrary';
import {NovaReferenceFilesProps, formatBytes, useNovaReferenceFiles} from './useNovaReferenceFiles';

const tokens = tokensForThemeProvider.sky.sys;

const WorkflowContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

const WorkflowLabelRow = styled.div`
	margin-bottom: ${tokens.spacing['200']};
`;

const WorkflowAcceptedRow = styled.div`
	margin-top: ${tokens.spacing['200']};
`;

const WorkflowDropInvitation = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.spacing['200']};
`;

const WorkflowFileList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.spacing['200']};
	margin-top: ${tokens.spacing['300']};
`;

// The drop target doubles as the Browse trigger's surround. A dashed border reads as "drop here"
// without needing a bespoke DS component; it highlights while a file is dragged over it.
const WorkflowDropZone = styled.div<{$active: boolean}>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: ${tokens.spacing['300']};
	min-height: 160px;
	padding: ${tokens.spacing['600']} ${tokens.spacing['400']};
	border: 1.5px dashed ${({$active}) => ($active ? NovaStudioColor.focusRing : NovaStudioColor.border)};
	border-radius: 8px;
	background-color: ${({$active}) => ($active ? NovaStudioColor.badgeBackground : 'transparent')};
`;

// Badge | filename + size | copy-token and Remove together. The actions column sizes to its content
// because the token has to stay readable in full — it is the text you are about to put on the
// clipboard — so the filename column is the one that gives way, truncating with an ellipsis.
const WorkflowRow = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	align-items: center;
	column-gap: ${tokens.spacing['300']};
	padding: ${tokens.spacing['200']} ${tokens.spacing['300']};
	border: 1px solid ${NovaStudioColor.border};
	border-radius: 8px;
`;

// Never wrapped: the token is a single copyable string, and breaking it across lines would both
// misrepresent it and make the button grow taller than the one beside it.
const WorkflowToken = styled.span`
	white-space: nowrap;
`;

const HiddenInput = styled.input`
	display: none;
`;

// A workflow's Files section on its Prompt tab: the same attach, remove and copy as a task agent's,
// laid out more compactly with smaller buttons and type, and a short confirmation beside a copied
// token.
export function NovaWorkflowReferenceFiles({files, onAttach, onRemove}: NovaReferenceFilesProps): JSX.Element {
	const {formatMessage} = useIntl();
	const {inputRef, dragActive, isBusy, error, copied, copyToken, remove, dropZoneHandlers, handleInputChange} =
		useNovaReferenceFiles({onAttach, onRemove});

	return (
		<WorkflowContainer data-cy="nova-reference-files" data-testid="nova-reference-files">
			<WorkflowLabelRow>
				<Label>{formatMessage({id: 'nova.referenceFiles.title'})}</Label>
			</WorkflowLabelRow>
			<WorkflowDropZone
				$active={dragActive}
				role="group"
				aria-label={formatMessage({id: 'nova.referenceFiles.title'})}
				data-cy="nova-reference-files-dropzone"
				{...dropZoneHandlers}
			>
				<Icon icon="upload" size="m" color="primary" />
				{/* While an upload is in flight the whole invitation is replaced by its progress, rather
					    than leaving "Drag a document here, or" standing next to a disabled button: the
					    drop zone is not accepting anything until this finishes, so still inviting a drop
					    would be telling the author something untrue. */}
				{isBusy ? (
					<Text color="auxiliary" data-cy="nova-reference-files-uploading">
						{formatMessage({id: 'nova.referenceFiles.uploading'})}
					</Text>
				) : (
					<WorkflowDropInvitation>
						<Text>{formatMessage({id: 'nova.referenceFiles.dropHint'})}</Text>
						<NovaButtonSizer $size="row">
							<Button
								emphasis="low"
								data-cy="nova-reference-files-browse"
								onPress={() => inputRef.current?.click()}
							>
								{formatMessage({id: 'nova.referenceFiles.browse'})}
							</Button>
						</NovaButtonSizer>
					</WorkflowDropInvitation>
				)}
				<HiddenInput
					ref={inputRef}
					type="file"
					accept={NOVA_FILE_ACCEPTED_EXTENSIONS.join(',')}
					data-cy="nova-reference-files-input"
					disabled={isBusy}
					onChange={handleInputChange}
				/>
			</WorkflowDropZone>
			<WorkflowAcceptedRow>
				<Detail size="1" color="auxiliary">
					{formatMessage({id: 'nova.referenceFiles.workflowAccepted'})}
				</Detail>
			</WorkflowAcceptedRow>
			{error && (
				<WorkflowAcceptedRow>
					<Text color="error" data-cy="nova-reference-files-error">
						{error}
					</Text>
				</WorkflowAcceptedRow>
			)}
			{files.length > 0 && (
				<WorkflowFileList>
					{files.map(file => (
						<WorkflowRow key={file.fileId} data-cy={`nova-reference-file-${file.fileId}`}>
							<Pill variant={PillVariant.NEUTRAL}>{fileTypeLabel(file.filename)}</Pill>
							<FlexColumn gap="xxs" alignItems="flex-start">
								<Text ellipsis>{file.filename}</Text>
								<Detail size="1" color="auxiliary">
									{formatBytes(file.sizeBytes)}
								</Detail>
							</FlexColumn>
							<FlexRow gap="s" alignItems="center">
								{file.processingStatus === NOVA_FILE_READY_STATUS && (
									// The token itself is the button: one target, labelled with exactly the text it
									// puts on the clipboard.
									<NovaButtonSizer $size="row">
										<Button
											emphasis="medium"
											data-cy={`nova-reference-file-copy-${file.fileId}`}
											title={formatMessage({id: 'nova.referenceFiles.copy'})}
											onPress={() => copyToken(file)}
										>
											<WorkflowToken>{file.variableToken}</WorkflowToken>
										</Button>
									</NovaButtonSizer>
								)}
								<span role="status" data-cy={`nova-reference-file-copy-status-${file.fileId}`}>
									{copied?.fileId === file.fileId && (
										<Text size="2" color={copied.ok ? 'auxiliary' : 'error'}>
											{formatMessage({
												id: copied.ok ? 'nova.referenceFiles.copied' : 'nova.referenceFiles.copyError',
											})}
										</Text>
									)}
								</span>
								<NovaButtonSizer $size="row">
									<Button
										emphasis="medium"
										data-cy={`nova-reference-file-remove-${file.fileId}`}
										disabled={isBusy}
										onPress={() => remove(file.fileId)}
									>
										{formatMessage({id: 'nova.referenceFiles.remove'})}
									</Button>
								</NovaButtonSizer>
							</FlexRow>
						</WorkflowRow>
					))}
				</WorkflowFileList>
			)}
		</WorkflowContainer>
	);
}
